import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Lock, User, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_tire-quote-portal/artifacts/nn6a64ea_IMG_1623.png";

export default function AdminSetup() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ username: "", password: "", email: "" });
  const [loading, setLoading] = useState(false);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password || !formData.email) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/admin/register`, formData);
      
      if (response.data.success) {
        toast.success("Compte administrateur créé avec succès");
        navigate("/admin");
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        toast.error("Un administrateur existe déjà");
      } else {
        toast.error("Erreur lors de la création du compte");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link 
          to="/admin" 
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
          data-testid="back-login-link"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Retour à la connexion</span>
        </Link>

        <div className="bg-zinc-900/30 border border-zinc-800 p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="PP Performance" className="h-16 mx-auto mb-6" />
            <h1 className="text-3xl font-bold uppercase tracking-tight">Configuration</h1>
            <p className="text-zinc-500 mt-2">Créer le premier compte administrateur</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-6" data-testid="admin-setup-form">
            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                Nom d'utilisateur
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input 
                  value={formData.username}
                  onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow pl-11"
                  placeholder="admin"
                  data-testid="input-setup-username"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input 
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow pl-11"
                  placeholder="admin@ppperformance.fr"
                  data-testid="input-setup-email"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 uppercase tracking-wider text-xs mb-2 block">
                Mot de passe
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input 
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  className="bg-zinc-900/50 border-zinc-800 focus:border-white input-glow pl-11"
                  placeholder="••••••••"
                  data-testid="input-setup-password"
                />
              </div>
              <p className="text-zinc-600 text-xs mt-2">Minimum 6 caractères</p>
            </div>

            <Button 
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-zinc-200 font-bold uppercase tracking-widest py-4 btn-mechanical"
              data-testid="admin-setup-btn"
            >
              {loading ? "Création..." : "Créer le compte"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
